import { useEffect, useMemo, useState } from "react";
import cc from "classcat";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Alert, Button, ContentCard, Row } from "@narmi/design_system";
import { cents, entities, modules } from "byzantine";
import filters from "byzantine/src/filters";
import { useFetchPaymentMetadataById } from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import { useIsACHDualApprovalRequired } from "byzantine/src/dbbl/businessLogic/entities/users/hooks";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import { PAYMENT_ACTIVITY_ROUTE } from "../../PaymentActivity/routes";
import { INVOICE_ROUTE } from "./InvoiceRoutes";
import { useInvoiceFlow } from "./InvoiceFlowContext";
import receiptStyles from "../ach/ReceiptScreen.module.scss";
import styles from "../funds/TransferReview.module.scss";
import invoiceReceiptStyles from "./ReceiptStep.module.scss";

const truncateStr = (value: string, maxLength = 20) =>
  value.length > maxLength ? `${value.substring(0, maxLength)}...` : value;

type ReceiptLocationState = {
  deliverySpeed?: string;
  deliveryFee?: string;
  speed?: string;
  paymentId?: string;
  paymentModel?:
    | "FedNowTransaction"
    | "UnverifiedACHTransaction"
    | "UnverifiedACHTransfer";
};

type ReceiptPaymentMetadata =
  | Pick<API.ACHPayment, "state">
  | Pick<API.FedNowTransaction, "state">;

const REJECTED_PAYMENT_STATES = new Set(["rejected", "declined"]);

const isRejectedReceiptPayment = (
  paymentMetadata: ReceiptPaymentMetadata | null
): boolean =>
  Boolean(
    paymentMetadata && REJECTED_PAYMENT_STATES.has(paymentMetadata.state)
  );

const ReceiptRow = ({
  name,
  value,
}: {
  name: string;
  value: React.ReactNode;
}) => (
  <Row>
    <Row.Item shrink>
      <p>{name}</p>
    </Row.Item>
    <Row.Item>
      <p
        style={{
          textAlign: "right",
          wordBreak: "break-word",
          overflowWrap: "break-word",
        }}
      >
        {value || "-"}
      </p>
    </Row.Item>
  </Row>
);

const ReceiptStep = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { state } = useLocation();
  const features = useUserFeatures();
  const form = modules.achPayments.ACHPaymentForm.useForm();
  const selectedRecipient = entities.recipients.useSelectedRecipient();
  const achDualApprovalRequired = useIsACHDualApprovalRequired();
  const { clearUploadedFile } = useInvoiceFlow();
  const receiptState = (state ?? {}) as ReceiptLocationState;
  const [paymentMetadata, setPaymentMetadata] =
    useState<ReceiptPaymentMetadata | null>(null);
  const { send: fetchPaymentMetadataById } = useFetchPaymentMetadataById();
  const paymentModel =
    receiptState.paymentModel ||
    (form.values.deliverySpeed === "instant"
      ? "FedNowTransaction"
      : "UnverifiedACHTransaction");

  useEffect(() => {
    if (!uuid) {
      return;
    }

    fetchPaymentMetadataById(uuid, paymentModel)
      .then((response) => {
        setPaymentMetadata(response as ReceiptPaymentMetadata);
      })
      .catch(() => {
        setPaymentMetadata(null);
      });
  }, [uuid, fetchPaymentMetadataById, paymentModel]);

  const isRejectedPayment = isRejectedReceiptPayment(paymentMetadata);
  const recipientName = selectedRecipient?.name || "-";

  const title = useMemo(() => {
    if (isRejectedPayment) {
      return l10n.getString("ach-payment-rejected-title");
    }

    if (achDualApprovalRequired) {
      return l10n.getString("ach-payment-receipt-dual-approval-title");
    }

    return l10n.getString("invoice-payment-receipt-title");
  }, [achDualApprovalRequired, isRejectedPayment, l10n]);

  if (!selectedRecipient) {
    return null;
  }

  let derivedSpeedLabel = `${l10n.getString("label-standard")} ACH`;
  if (form.values.deliverySpeed === "instant") {
    derivedSpeedLabel = l10n.getString("label-instant");
  } else if (form.values.deliverySpeed === "sameDay") {
    derivedSpeedLabel = l10n.getString("label-same-day");
  }

  let derivedDeliveryArrival = l10n.getString("label-ach-speed-short");
  if (form.values.deliverySpeed === "instant") {
    derivedDeliveryArrival = l10n.getString("label-instant");
  } else if (form.values.deliverySpeed === "sameDay") {
    derivedDeliveryArrival = l10n.getString(
      "label-same-day-delivery-speed-arrival"
    );
  }

  const paymentId = truncateStr(receiptState.paymentId || uuid || "-", 35);
  const deliveryFee =
    receiptState.deliveryFee || l10n.getString("label-no-fee");
  const deliverySpeed = receiptState.deliverySpeed || derivedDeliveryArrival;
  const speed = receiptState.speed || derivedSpeedLabel;
  const shouldShowPaymentId = paymentModel === "FedNowTransaction";

  return (
    <div className={invoiceReceiptStyles.receiptContainer}>
      <ContentCard>
        <div className={receiptStyles.cardContainer}>
          <h2 className={invoiceReceiptStyles.title}>
            {title}
            <span
              className={`narmi-icon-${
                isRejectedPayment ? "x" : "check"
              } margin--left--xs`}
            />
          </h2>
          {isRejectedPayment ? (
            <div className={receiptStyles.rejectedAlert}>
              <Alert isActive isDismissable={false} kind="error">
                {l10n.getString("ach-payment-rejected-alert")}
              </Alert>
            </div>
          ) : null}
          {achDualApprovalRequired ? (
            <div className={receiptStyles.dualApprovalAlert}>
              <Alert isActive isDismissable={false} kind="warn">
                {l10n.getString("invoice-payment-receipt-dual-approval-alert")}
              </Alert>
            </div>
          ) : null}

          <div
            data-testid="invoice-payment-receipt-summary"
            className={cc([
              invoiceReceiptStyles.summaryCard,
              {
                [invoiceReceiptStyles.summaryCardRejected]: isRejectedPayment,
              },
            ])}
          >
            <ReceiptRow
              name={l10n.getString("single-ach-payment-recipient-send-to")}
              value={recipientName}
            />
            <ReceiptRow
              name={l10n.getString("label-amount")}
              value={filters.currency(cents(form.values.amount))}
            />
            <ReceiptRow
              name={l10n.getString("label-delivery-speed")}
              value={deliverySpeed}
            />
            <ReceiptRow
              name={l10n.getString("label-fee")}
              value={deliveryFee}
            />
            <ReceiptRow name={l10n.getString("speed-label")} value={speed} />
            {shouldShowPaymentId ? (
              <div
                className={invoiceReceiptStyles.paymentIdRow}
                data-testid="invoice-payment-receipt-payment-id"
              >
                <span className={invoiceReceiptStyles.paymentIdLabel}>
                  {l10n.getString("label-payment-id")}
                </span>
                <span className={invoiceReceiptStyles.paymentIdValue}>
                  {paymentId}
                </span>
              </div>
            ) : null}
          </div>

          <div className={receiptStyles.buttonContainer}>
            <div className={styles.actionButtons}>
              <Button
                kind="tertiary"
                label={l10n.getString("ach-payment-receipt-cta")}
                testId="invoice-payment-receipt-cta-button"
                onClick={() => {
                  clearUploadedFile();
                  form.resetForm();
                  navigate(`/transfer/${INVOICE_ROUTE}`);
                }}
              />
              <Button
                label={l10n.getString("label-done")}
                testId="invoice-payment-receipt-done"
                onClick={() => {
                  if (features.payment_activity && uuid) {
                    navigate(`/${PAYMENT_ACTIVITY_ROUTE}/${uuid}`);
                    return;
                  }

                  navigate("/transfer");
                }}
              />
            </div>
          </div>
        </div>
      </ContentCard>
    </div>
  );
};

export default ReceiptStep;
